import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Car, Check, Calendar, Clock, MapPin, User, Mail, Phone, ArrowLeft, Loader2, Home, Building2 } from "lucide-react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Header = () => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-zinc-800">
    <div className="container-custom">
      <div className="flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
            <Car className="w-6 h-6 text-black" />
          </div>
          <span className="text-xl font-medium tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Esthétique Auto TR
          </span>
        </Link>
      </div>
    </div>
  </header>
);

const ConfirmationPage = () => {
  const { appointmentId } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAppointment = async () => {
      try {
        const response = await axios.get(`${API}/appointments/${appointmentId}`);
        setAppointment(response.data);
      } catch (err) {
        console.error("Error fetching appointment:", err);
        setError("Réservation introuvable");
      } finally {
        setLoading(false);
      }
    };
    fetchAppointment();
  }, [appointmentId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black" data-testid="confirmation-error">
        <Header />
        <main className="pt-32 pb-16">
          <div className="container-custom text-center">
            <h1 className="text-2xl text-white mb-4">{error}</h1>
            <Link to="/">
              <Button className="bg-white text-black hover:bg-zinc-200">
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const formattedDate = appointment?.date 
    ? format(parseISO(appointment.date), "EEEE d MMMM yyyy", { locale: fr })
    : "";

  return (
    <div className="min-h-screen bg-black" data-testid="confirmation-page">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            
            {/* Success Icon */}
            <div className="text-center mb-8 fade-in">
              <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-6">
                <Check className="w-10 h-10 text-green-500" />
              </div>
              <span className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4 inline-block">Confirmation</span>
              <h1 className="text-3xl sm:text-4xl tracking-tighter font-medium text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Réservation Confirmée !
              </h1>
              <p className="text-zinc-400 mt-4">
                Merci pour votre confiance. J'ai bien reçu votre demande de rendez-vous.
              </p>
            </div>

            {/* Appointment Details */}
            <Card className="bg-zinc-900 border-zinc-800 mb-6 slide-up" data-testid="appointment-details">
              <CardContent className="p-6">
                <h2 className="text-lg font-medium text-white mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  Détails de votre Rendez-vous
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-black border border-zinc-800">
                    <Car className="w-5 h-5 text-white mt-0.5" />
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Service</p>
                      <p className="text-white font-medium">{appointment?.service_name}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-start gap-4 p-4 rounded-lg bg-black border border-zinc-800">
                      <Calendar className="w-5 h-5 text-white mt-0.5" />
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">Date</p>
                        <p className="text-white font-medium capitalize">{formattedDate}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 rounded-lg bg-black border border-zinc-800">
                      <Clock className="w-5 h-5 text-white mt-0.5" />
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">Heure</p>
                        <p className="text-white font-medium">{appointment?.time_slot}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                    {appointment?.location_type === 'garage' ? (
                      <Building2 className="w-5 h-5 text-white mt-0.5" />
                    ) : (
                      <Home className="w-5 h-5 text-white mt-0.5" />
                    )}
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Lieu</p>
                      <p className="text-white font-medium">
                        {appointment?.location_type === 'garage' ? 'En garage' : 'Service à domicile'}
                      </p>
                      {appointment?.address && (
                        <p className="text-sm text-zinc-400 mt-1">{appointment.address}</p>
                      )}
                    </div>
                  </div>

                  {appointment?.selected_extras && appointment.selected_extras.length > 0 && (
                    <div className="flex items-start gap-4 p-4 rounded-lg bg-black border border-zinc-800">
                      <Check className="w-5 h-5 text-green-500 mt-0.5" />
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">Options supplémentaires</p>
                        <ul className="mt-1">
                          {appointment.selected_extras.map((extra, i) => (
                            <li key={i} className="text-white text-sm">• {extra}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {(appointment?.vehicle_brand || appointment?.vehicle_model) && (
                    <div className="flex items-start gap-4 p-4 rounded-lg bg-black border border-zinc-800">
                      <Car className="w-5 h-5 text-white mt-0.5" />
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">Véhicule</p>
                        <p className="text-white font-medium">
                          {appointment?.vehicle_brand} {appointment?.vehicle_model}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card className="bg-zinc-900 border-zinc-800 mb-8 slide-up" style={{ animationDelay: '0.1s' }}>
              <CardContent className="p-6">
                <h2 className="text-lg font-medium text-white mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  Vos Coordonnées
                </h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-white" />
                    <div>
                      <p className="text-xs text-zinc-500">Nom</p>
                      <p className="text-white text-sm">{appointment?.client_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-white" />
                    <div>
                      <p className="text-xs text-zinc-500">Courriel</p>
                      <p className="text-white text-sm">{appointment?.client_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-white" />
                    <div>
                      <p className="text-xs text-zinc-500">Téléphone</p>
                      <p className="text-white text-sm">{appointment?.client_phone}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Next Steps */}
            <div className="text-center p-6 rounded-xl bg-zinc-900 border border-zinc-800 slide-up" style={{ animationDelay: '0.2s' }}>
              <h3 className="text-white font-medium mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Prochaines étapes
              </h3>
              <p className="text-sm text-zinc-400 mb-6">
                Je vous contacterai pour confirmer votre rendez-vous. 
                Un courriel de confirmation vous a également été envoyé.
              </p>
              <Link to="/">
                <Button 
                  className="bg-white text-black hover:bg-zinc-200 font-medium px-8"
                  data-testid="back-home-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour à l'accueil
                </Button>
              </Link>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default ConfirmationPage;
